import DeleteIcon from '@mui/icons-material/Delete';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { AppBar, Autocomplete, Box, Button, Divider, IconButton, ListItem, ListItemText, TextField, Toolbar, Typography, useMediaQuery, type Theme } from '@mui/material';
import { useFormik } from 'formik';
import { useAtom } from 'jotai';
import { SyntheticEvent, useEffect } from 'react';
import * as yup from 'yup';
import { type Application, type Criterion } from '../../../../shared/types';
import { drawerStateAtom } from '../../../atoms';
import { useAdminApplications, useCreateAdminApplication, useDeleteAdminApplication, useUpdateAdminApplication } from '../../../hooks/useAdminApplications';
import { useDeleteDialog } from '../../../hooks/useDeleteDialog';
import { useSimpleAlert } from '../../../hooks/useSimpleAlert';
import { CriterionList } from '../../CriterionList';
import { closedMixinToolbar, openedMixinToolbar } from '../../helpers';
import { useNavigate } from 'react-router';

type Props = {
    application?: Application | null;
};

const initialValues = {
    name: '',
    folder: '',
    specsheet: '',
    yellowCriterion: [] as Criterion[],
    redCriterion: [] as Criterion[],
} as Application;

const criterionSchema = yup.object().shape({
    threshold: yup.number().required(),
    criteria: yup.object().shape({
        max: yup.number().required(),
        min: yup.number().required(),
        unit: yup.string().required(),
        value: yup.string().required(),
        text: yup.string().required(),
        label: yup.string().required(),
    }),
});
const validationSchema = yup.object().shape({
    name: yup.string().required('Required'),
    folder: yup.string().required('Required'),
    specsheet: yup.string().required('Required'),
    yellowCriterion: yup.array().of(criterionSchema).required(),
    redCriterion: yup.array().of(criterionSchema).required(),
});

export const ApplicationForm = ({ application = null }: Props) => {
    const navigate = useNavigate();
    const [drawerState] = useAtom(drawerStateAtom);
    const isNotSm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const { data: applications = [], isLoading } = useAdminApplications();
    const { mutate: updateAdminApplication } = useUpdateAdminApplication();
    const { mutate: createAdminApplication } = useCreateAdminApplication();
    const { mutate: deleteAdminApplication } = useDeleteAdminApplication();
    const { DeleteDialog, showDialog: showDeleteDialog } = useDeleteDialog();
    const { showSimpleAlert } = useSimpleAlert();
    const folders = new Set(applications.map(({ folder }) => folder).sort());

    const onBack = () => {
        navigate('/admin/applications');
    };

    const onSubmit = async (values: Application) => {
        const { _id } = values;
        const toStrip = {
            ...values,
            redCriterion: values.redCriterion || [],
            yellowCriterion: values.yellowCriterion || [],
        };
        const strippedValues = validationSchema.cast(toStrip, { stripUnknown: true });

        if (_id) {
            updateAdminApplication(
                { id: _id.toString(), data: strippedValues },
                {
                    onSuccess: (data) => {
                        showSimpleAlert({ title: 'Application successfully updated' });
                        formik.resetForm({ values: data });
                    },
                },
            );
        } else {
            createAdminApplication(strippedValues, {
                onSuccess: () => {
                    showSimpleAlert({ title: 'Application successfully created' });
                    formik.resetForm({ values: initialValues });
                },
            });
        }
    };

    const formik = useFormik({ initialValues, validationSchema, onSubmit });

    const handleShowDeleteDialog = () => {
        showDeleteDialog({
            title: `Confirm DELETE of application ${application?.name}`,
            onDelete: handleDeleteApplication,
        });
    };

    const handleDeleteApplication = () => {
        if (application) {
            deleteAdminApplication(application._id.toString(), {
                onSuccess: () => {
                    showSimpleAlert({ title: 'Application successfully deleted', severity: 'info' });
                    onBack();
                },
                onError: () => {
                    showSimpleAlert({ title: 'Unable to delete the application', severity: 'error' });
                },
            });
        }
    };

    const handleRedCriterionsChange = (criterions: Criterion[]) => {
        formik.setFieldValue('redCriterion', criterions);
    };

    const handleYellowCriterionsChange = (criterions: Criterion[]) => {
        formik.setFieldValue('yellowCriterion', criterions);
    };

    const handleFolderChange = (_: SyntheticEvent<Element, Event>, folder: string | null) => {
        const match = folder?.match(/Add "(.*)"$/);

        if (match) {
            folder = match[1];
        }

        formik.setFieldValue('folder', folder);
    };

    useEffect(() => {
        const values = application ? { ...initialValues, ...application } : initialValues;

        formik.resetForm({ values });
    }, [application]);

    return (
        <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ maxWidth: (theme) => theme.breakpoints.values.sm, p: 3, pb: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 2 }}>
                        <Button variant="outlined" size="large" sx={{ minWidth: 0, pl: 1, pr: 1 }} onClick={onBack} startIcon={<NavigateBeforeIcon />}>
                            Applications
                        </Button>
                        {application && (
                            <Typography variant="h6" gutterBottom={false}>
                                {`${application.name}`}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                        Application Details
                    </Typography>
                    <Divider />
                </Box>
                <Box
                    sx={{
                        height: '100%',
                        overflowY: 'scroll',
                    }}>
                    <form noValidate autoComplete="off">
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                maxWidth: (theme) => theme.breakpoints.values.sm,
                                p: 3,
                                mb: 8,
                            }}>
                            <Autocomplete
                                freeSolo={true}
                                selectOnFocus={true}
                                clearOnBlur={true}
                                options={Array.from(folders) ?? []}
                                filterOptions={(options, params) => {
                                    const { inputValue } = params;
                                    const isExisting = options.some((option) => inputValue === option);

                                    if (inputValue.length > 0 && !isExisting) {
                                        return [`Add "${inputValue}"`];
                                    }

                                    return options;
                                }}
                                renderOption={(props: object, folder: string) => {
                                    return (
                                        <ListItem {...props}>
                                            <ListItemText primary={folder} />
                                        </ListItem>
                                    );
                                }}
                                renderInput={(params) => <TextField {...params} variant="outlined" label="Folder" error={Boolean(formik.errors.folder)} helperText={formik.touched.name && formik.errors.folder} />}
                                loading={isLoading}
                                onChange={handleFolderChange}
                                value={formik.values.folder}
                            />
                            <TextField
                                aria-describedby="name-text"
                                label="Name"
                                placeholder="Name"
                                type="text"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                            <TextField
                                aria-describedby="specsheet-text"
                                label="Spec Sheet"
                                placeholder="Spec Sheet"
                                type="text"
                                name="specsheet"
                                value={formik.values.specsheet}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.specsheet)}
                                helperText={formik.touched.specsheet && formik.errors.specsheet}
                            />
                            <CriterionList label="Unfavorable Criteria" severity="error" edit={true} onChange={handleRedCriterionsChange} criterion={formik.values.redCriterion} />
                            <CriterionList label="Questionable Criteria" severity="warning" edit={true} onChange={handleYellowCriterionsChange} criterion={formik.values.yellowCriterion} />
                        </Box>
                    </form>
                </Box>
            </Box>
            <AppBar position="fixed" sx={{ top: 'auto', bottom: 0, backgroundColor: (theme) => theme.palette.grey[50] }}>
                <Toolbar
                    sx={(theme) => ({
                        ...(isNotSm &&
                            drawerState && {
                                ...openedMixinToolbar(theme),
                            }),
                        ...(isNotSm &&
                            !drawerState && {
                                ...closedMixinToolbar(theme),
                            }),
                        maxWidth: (theme) => theme.breakpoints.values.sm,
                    })}>
                    {application && (
                        <IconButton aria-label="delete" size="large" onClick={handleShowDeleteDialog}>
                            <DeleteIcon fontSize="inherit" />
                        </IconButton>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" onClick={formik.submitForm} disabled={!formik.isValid || !formik.dirty}>
                        Save
                    </Button>
                </Toolbar>
            </AppBar>
            <DeleteDialog />
        </Box>
    );
};
